/* eslint-disable max-lines */

import * as cssColorConverter from 'css-color-converter'

import { APR_POSTFIX, ContactEven, ContactFiona, ContactLeapLife, FEE_PREFIX } from 'embed/constants'
import {
  APPLICATION_FIELD_QUERY_PARAM_PREFIX,
  Brand,
  Config,
  CreditCardPurposesOptions,
  DefaultEvenConfig,
  DefaultEvenTheme,
  DefaultFionaConfig,
  DefaultFionaTheme,
  DefaultLeapLifeConfig,
  DefaultLeapLifeTheme,
  EVEN_MESSAGE_PREFIX,
  Filter,
  FILTER_QUERY_PARAM_PREFIX,
  FIONA_MESSAGE_PREFIX,
  LEAPLIFE_MESSAGE_PREFIX,
  RewardsPreferenceTypeOptions,
} from '@evenfinancial/even-ts-static'
import { getAprDisplay } from './offer-display-utils'

// Returns a boolean value of the state key
export function getBool(value): boolean {
  if (value !== undefined) {
    return value === 'true' || value === true ? true : false
  }

  return false
}

// Returns a boolean from a boolean string
export function getBooleanFromBooleanString(value: string) {
  return value === 'true'
}

// Given a state key, strips non-numeric characters and returns integer value, or undefined if it isn't found
export function getIntFromString(value?: string): number | undefined {
  if (value !== undefined && typeof value === 'string') {
    return parseInt(value.replace(/[^0-9.]/g, ''), 10)
  }

  return value
}

// Returns a YYYY-MM-DD formatted date string of the state key or undefined if it isn't found
export function getDateStringMonthAndYearOnly(value?: string): string | undefined {
  if (value !== undefined) {
    const matches = value.match(/^(0?[1-9]|1[012])[\/\-](\d{4})$/)
    if (matches !== null) {
      return `${matches[2]}-${matches[1]}-01`
    }
  }

  return value
}

// Returns a YYYY-MM-DD formatted date string of the state key or undefined if it isn't found
export function getDateString(value?: string): string | undefined {
  if (value !== undefined) {
    const matches = value.match(/^(0?[1-9]|1[012])[\/\-](0?[1-9]|[12][0-9]|3[01])[\/\-](\d{4})$/)
    if (matches !== null) {
      return `${matches[3]}-${matches[1]}-${matches[2]}`
    }
  }

  return value
}

/**
 *  Returns the value of an offer field, or N/A if the value is -1
 */
export interface OfferNumericFieldValueOptions {
  prefix?: string | undefined
  fallback?: string | undefined
  postfix?: string | undefined
  transformValue?: (value: number) => unknown | undefined
}
export const getOfferNumericFieldValue = (fieldValue, options: OfferNumericFieldValueOptions = {}) => {
  const { prefix = '', fallback, postfix = '' } = options
  const transformedFieldValue =
    fieldValue !== undefined && options.transformValue !== undefined ? options.transformValue(fieldValue) : fieldValue

  if (fieldValue !== undefined) {
    return fieldValue === -1 ? 'N/A' : `${prefix}${transformedFieldValue}${postfix}`
  } else {
    return fallback
  }
}

export function getUrlParamByName(url: string, name: string): string | undefined {
  const paramName = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]')
  const regex = new RegExp(`[\\?&]${paramName}=([^&#]*)`)
  const results = regex.exec(url)
  return results === null ? undefined : decodeURIComponent(results[1].replace(/\+/g, ' '))
}

export function originFromUrl(url: string): string {
  const parsedUrl = new URL(url)

  let origin = `${parsedUrl.protocol}//${parsedUrl.hostname}`

  if (parsedUrl.port.length > 0) {
    origin = `${origin}:${parsedUrl.port}`
  }

  return origin
}

export function buildUrl(url: string, params = {}) {
  // Add any extra query parameters.
  const absoluteUrl = new URL(url)
  for (const key in params) {
    absoluteUrl.searchParams.append(key, params[key])
  }
  return absoluteUrl
}

export function formatImageUrl(url?: string): string {
  if (url === undefined) {
    // If no image is provided, use a 1px transparent png instead
    // of the default browser broken image asset
    return 'https://images.evenfinancial.com/icons/1px.png'
  } else {
    // Prepend URLs starting with // with https:
    if (url.startsWith('//')) {
      return `https:${url}`
    }

    // If existing protocols exist, use them otherwise add https://
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    } else {
      return `https://${url}`
    }
  }
}

// Returns the value for filter.{key} or app.{key} if they exist in config
export const getFilterOrApp = (config: Config, key: Filter) =>
  config[`${FILTER_QUERY_PARAM_PREFIX}${key}`] ?? config[`${APPLICATION_FIELD_QUERY_PARAM_PREFIX}${key}`]

export const padCurrency = (num: number) => num.toFixed(2)
export const padRate = (num: number) => num.toFixed(2)

export const padCurrencyShort = (num: number | undefined | null) => {
  // eslint-disable-next-line eqeqeq
  if (num != null) {
    try {
      return num === parseInt(num.toString(), 10) ? num.toString() : padCurrency(num)
    } catch (err) {
      return num
    }
  } else {
    return num
  }
}

export const numberWithThousandsSeparators = (num?: number | string | null): string | undefined =>
  num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')

export const formatCurrency = (num: number) => numberWithThousandsSeparators(padCurrencyShort(num))

export const formatCurrencyWithDecimals = (num: number) => numberWithThousandsSeparators(padCurrency(num))

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function parseObjectBools(object: object, keys: string[]): any {
  const result = { ...object }

  keys.forEach((key) => {
    const value = object[key]
    if (value === 'true') {
      result[key] = true
    } else if (value === 'false') {
      result[key] = false
    } else {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete result[key]
    }
  })

  return result
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function parseObjectJsons(object: object, keys: string[]): any {
  const result = { ...object }

  keys.forEach((key) => {
    try {
      result[key] = JSON.parse(object[key])
    } catch {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete result[key]
    }
  })

  return result
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function parseObjectNumbers(object: object, keys: string[]): any {
  const result = { ...object }

  keys.forEach((key) => {
    const value = +object[key]
    if (isNaN(value)) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete result[key]
    } else {
      result[key] = value
    }
  })

  return result
}

export function pluralize(root: string, count: number) {
  // This is a naive implementation of pluralization. If a better implementation is needed,
  // consider replacing it with https://www.npmjs.com/package/pluralize or similar
  return count === 1 ? root : `${root}s`
}

export function urlSerialize(obj: object | string | number | boolean) {
  if (typeof obj !== 'object') {
    return encodeURIComponent(obj)
  }

  return Object.keys(obj)
    .filter((key) => obj.hasOwnProperty(key) && obj[key] !== undefined && obj[key] !== null)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`)
    .join('&')
}

export function formattedPhoneNumber(phoneNumber: string) {
  if (phoneNumber.length === 10) {
    // Phone number format: (800) 555-1234
    return phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
  } else {
    return phoneNumber
  }
}

export const generateRateString = (min?: number, max?: number, term?: number, units?: string): string | undefined => {
  if (min !== undefined && max !== undefined && term !== undefined && units !== undefined) {
    if (min === max) {
      return `${min}${APR_POSTFIX} for ${term} ${pluralize(units, term)}`
    } else {
      return `${min}${APR_POSTFIX} - ${max}${APR_POSTFIX} for ${term} ${pluralize(units, term)}`
    }
  } else if (min !== undefined && max !== undefined) {
    if (min === max) {
      return `${min}${APR_POSTFIX}`
    } else {
      return `${min}${APR_POSTFIX} - ${max}${APR_POSTFIX}`
    }
  } else if (min !== undefined) {
    return `${min}${APR_POSTFIX}`
  } else if (max !== undefined) {
    return `${max}${APR_POSTFIX}`
  } else {
    return undefined
  }
}

const formatAnnualFee = (annualIntroFee, annualIntroFeeTerm, formattedYear, amount, feeDifference = '') => {
  if (annualIntroFee === undefined || annualIntroFeeTerm === undefined) {
    return null
  }
  return `${FEE_PREFIX}${formatCurrency(
    annualIntroFee,
  )} for ${annualIntroFeeTerm} ${formattedYear}, then ${FEE_PREFIX}${formatCurrency(amount)}${feeDifference}`
}

export const generateAnnualFeeString = (
  min?: number,
  max?: number,
  legacyValue?: number,
  annualIntroFee?: number,
  annualIntroFeeTerm?: number,
): string | undefined => {
  const formattedYear = annualIntroFeeTerm === 1 ? 'year' : 'years'

  if (min !== undefined && max !== undefined) {
    const feeDifference = min === max ? '' : ` - ${FEE_PREFIX}${max}`
    return (
      formatAnnualFee(annualIntroFee, annualIntroFeeTerm, formattedYear, min, feeDifference) ??
      `${FEE_PREFIX}${formatCurrency(min)}${feeDifference}`
    )
  } else {
    const amount = max ?? min ?? legacyValue
    if (!amount) {
      return undefined
    }
    return formatAnnualFee(annualIntroFee, annualIntroFeeTerm, formattedYear, amount) ?? `${FEE_PREFIX}${amount}`
  }
}

export const purchaseAprString = (min?: number, max?: number, purchaseAprText?: string): string | undefined => {
  if (purchaseAprText !== undefined) {
    return purchaseAprText
  } else if (min !== undefined && max !== undefined) {
    if (min === max) {
      return `${getAprDisplay(min)}${APR_POSTFIX}`
    } else {
      return `${getAprDisplay(min)}${APR_POSTFIX} - ${' '}${getAprDisplay(max)}${APR_POSTFIX}`
    }
  } else if (min !== undefined) {
    return `${getAprDisplay(min)}${APR_POSTFIX}`
  } else if (max !== undefined) {
    return `${getAprDisplay(max)}${APR_POSTFIX}`
  } else {
    return undefined
  }
}

export const hasScriptLoaded = (script: string): boolean => {
  const scripts = document.getElementsByTagName('script')
  return !!Object.values(scripts).find((el) => el.src === script)
}

export const getCreditCardPurposeOrGroup = (
  purpose?: CreditCardPurposesOptions | null,
): CreditCardPurposesOptions[] | undefined => {
  if (purpose) {
    switch (purpose) {
      case CreditCardPurposesOptions.EARNING_REWARDS:
        return [
          CreditCardPurposesOptions.CASH_BACK,
          CreditCardPurposesOptions.EARNING_REWARDS,
          CreditCardPurposesOptions.TRAVEL_INCENTIVES,
        ]
      case CreditCardPurposesOptions.LOW_INTEREST:
        return [CreditCardPurposesOptions.BALANCE_TRANSFER, CreditCardPurposesOptions.LOW_INTEREST]
      case CreditCardPurposesOptions.NEW_TO_CREDIT:
        return [
          CreditCardPurposesOptions.IMPROVE_CREDIT,
          CreditCardPurposesOptions.NEW_TO_CREDIT,
          CreditCardPurposesOptions.STUDENT,
        ]
      default:
        return [purpose]
    }
  }
  return undefined
}

export const mapRedemptionTypeFromLabelValue = (
  rewardsPreference?: string | null,
): RewardsPreferenceTypeOptions | undefined =>
  rewardsPreference !== undefined && rewardsPreference !== null
    ? RewardsPreferenceTypeOptions[rewardsPreference.toUpperCase().replace(/ /g, '_')]
    : undefined

enum BlackAndWhite {
  Black = '#000',
  White = '#fff',
}
interface GetContrastingColorOptions {
  white?: string
  black?: string
}
type GetContrastingColorReturnValue = BlackAndWhite | GetContrastingColorOptions[keyof GetContrastingColorOptions]
export const getContrastingColor = (
  backgroundColor?: string,
  options?: GetContrastingColorOptions,
): GetContrastingColorReturnValue => {
  const white = options?.white ?? BlackAndWhite.White
  const black = options?.black ?? BlackAndWhite.Black

  let textColor: GetContrastingColorReturnValue
  try {
    const hexColor = cssColorConverter((backgroundColor as string).toLowerCase()).toHexString()
    // The following is adapted from here: https://medium.com/@druchtie/contrast-calculator-with-yiq-5be69e55535c
    const r = parseInt(hexColor.substr(1, 2), 16)
    const g = parseInt(hexColor.substr(3, 2), 16)
    const b = parseInt(hexColor.substr(5, 2), 16)
    const yiq = (r * 299 + g * 587 + b * 114) / 1000
    if (yiq >= 180) {
      textColor = black
    } else {
      textColor = white
    }
  } catch (_) {
    textColor = white
  }
  return textColor
}

const getRgbFromRgba = (color: number, alpha: number) => (1 - alpha) * 255 + alpha * color

export const getOpacityColor = (color: string | undefined, percentage: number): string => {
  const originalColor = color ?? 'white'
  let hexColor: string
  const alpha = percentage * 0.01
  try {
    hexColor = cssColorConverter(originalColor.toLowerCase()).toHexString()
  } catch (_) {
    hexColor = originalColor
  }
  const rgbValue = hexToRgb(hexColor) ?? { b: 255, g: 255, r: 255 }
  return `rgb(${getRgbFromRgba(rgbValue.r, alpha)}, ${getRgbFromRgba(rgbValue.g, alpha)}, ${getRgbFromRgba(
    rgbValue.b,
    alpha,
  )})`
}

interface RGBObject {
  r: number
  g: number
  b: number
}

export const hexToRgb = (hex: string): RGBObject | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? {
        b: parseInt(result[3], 16),
        g: parseInt(result[2], 16),
        r: parseInt(result[1], 16),
      }
    : null
}

const evenBrandObject = {
  contact: ContactEven,
  defaultConfig: DefaultEvenConfig,
  defaultTheme: DefaultEvenTheme,
  domain: 'evenfinancial.com',
  messagePrefix: EVEN_MESSAGE_PREFIX,
  version: '7',
} as const
const fionaBrandObject = {
  contact: ContactFiona,
  defaultConfig: DefaultFionaConfig,
  defaultTheme: DefaultFionaTheme,
  domain: 'hifiona.com',
  messagePrefix: FIONA_MESSAGE_PREFIX,
  version: '8',
} as const
const leaplifeBrandObject = {
  contact: ContactLeapLife,
  defaultConfig: DefaultLeapLifeConfig,
  defaultTheme: DefaultLeapLifeTheme,
  domain: 'leaplife.com',
  messagePrefix: LEAPLIFE_MESSAGE_PREFIX,
  version: '9',
} as const
export type BrandObject = typeof evenBrandObject | typeof fionaBrandObject | typeof leaplifeBrandObject

export const getBrandObject = (brand?: Brand): BrandObject =>
  brand === Brand.Even ? evenBrandObject : brand === Brand.LeapLife ? leaplifeBrandObject : fionaBrandObject

export const capitalize = (str: string): string => str.charAt(0).toUpperCase() + str.substring(1)

// https://github.com/30-seconds/30-seconds-of-code/blob/master/snippets/toSnakeCase.md
export const snakeCase = (str: string): string => {
  const words = str.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
  return words ? words.map((x) => x.toLowerCase()).join('_') : ''
}

export const isBrowser = (): boolean => typeof window !== 'undefined'

export const lazyLoadScript = (scriptId: string, scriptSrc: string) =>
  new Promise<void>((resolve, reject) => {
    if (isBrowser()) {
      if (document.getElementById(scriptId) === null) {
        const helper = document.createElement('script')
        helper.id = scriptId
        helper.src = scriptSrc
        helper.async = true
        helper.defer = true
        helper.setAttribute('importance', 'low')
        helper.onload = () => {
          resolve()
        }
        helper.onerror = (event, source, line, col, error) => reject(error || event)
        document.head.appendChild(helper)
      } else {
        reject(new Error())
      }
    } else {
      reject(new Error())
    }
  })
